import React, { useState } from 'react';
import {
  MessageSquare,
  Calendar,
  Eye,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Button, Input, SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import ContactMessageDetailModal from '../../components/admin/ContactMessageDetailModal';
import { useAdminContactMessages } from '../../hooks/useAdminContactMessages';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { useToastContext } from '../../contexts/ToastContext';

const AdminContactMessages = () => {
  const toast = useToastContext();
  const [selectedMessage, setSelectedMessage] = useState(null);
  const {
    messages,
    isLoading,
    error,
    filters,
    meta,
    statistics,
    handleFilterChange,
    handlePageChange,
    updateMessage,
    deleteMessage,
    replyMessage,
    isReplying,
    INQUIRY_LABELS,
    STATUS_LABELS,
  } = useAdminContactMessages();

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'read':
        return 'bg-gray-100 text-gray-800';
      case 'replied':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'From',
      sortable: false,
      width: 'w-48',
      render: (msg) => (
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{msg.name}</p>
          <p className="text-xs text-gray-500 truncate">{msg.email}</p>
        </div>
      ),
    },
    {
      key: 'subject',
      label: 'Subject',
      sortable: false,
      width: 'w-48',
      render: (msg) => (
        <p className="text-sm font-medium text-gray-900 truncate">{msg.subject}</p>
      ),
    },
    {
      key: 'inquiry_type',
      label: 'Type',
      sortable: false,
      width: 'w-32',
      render: (msg) => (
        <span className="text-sm text-gray-600">
          {INQUIRY_LABELS[msg.inquiry_type] || msg.inquiry_type}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: false,
      width: 'w-24',
      render: (msg) => (
        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(msg.status)}`}>
          {STATUS_LABELS[msg.status] || msg.status}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: false,
      width: 'w-32',
      render: (msg) => (
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium text-gray-900">{formatDate(msg.created_at, 'medium')}</div>
            <div className="text-xs text-gray-500">{formatTime(msg.created_at)}</div>
          </div>
        </div>
      ),
    },
  ];

  const actions = [
    {
      label: 'View',
      icon: Eye,
      onClick: (msg) => setSelectedMessage(msg),
    },
    {
      label: 'Delete',
      icon: Trash2,
      onClick: (msg) => {
        if (window.confirm('Are you sure you want to delete this message?')) {
          deleteMessage(msg.id);
          toast.success(null, 'Message deleted');
          if (selectedMessage?.id === msg.id) setSelectedMessage(null);
        }
      },
      className: 'text-red-600 hover:text-red-700',
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <MessageSquare className="w-8 h-8" />
          Contact Messages
        </h1>
        <p className="mt-2 text-gray-600">Messages submitted via the Contact Us form</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <p className="text-sm text-gray-500">Total</p>
          <p className="text-2xl font-bold text-gray-900">{statistics.total}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-blue-200 bg-blue-50/30">
          <p className="text-sm text-blue-600">New</p>
          <p className="text-2xl font-bold text-blue-700">{statistics.new}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <p className="text-sm text-gray-500">Read</p>
          <p className="text-2xl font-bold text-gray-900">{statistics.read}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-green-200 bg-green-50/30">
          <p className="text-sm text-green-600">Replied</p>
          <p className="text-2xl font-bold text-green-700">{statistics.replied}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <Input
              placeholder="Search name, email, subject..."
              value={filters.search || ''}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full"
            />
          </div>
          <div className="w-40">
            <SearchSelect
              label="Status"
              options={[
                { value: '', label: 'All' },
                { value: 'new', label: 'New' },
                { value: 'read', label: 'Read' },
                { value: 'replied', label: 'Replied' },
              ]}
              value={filters.status || ''}
              onChange={(val) => handleFilterChange('status', val)}
              placeholder="All"
              searchable={false}
              clearable
            />
          </div>
          <div className="w-48">
            <SearchSelect
              label="Inquiry Type"
              options={[
                { value: '', label: 'All' },
                { value: 'general', label: 'General' },
                { value: 'booking', label: 'Booking' },
                { value: 'technical', label: 'Technical' },
                { value: 'vendor', label: 'Vendor' },
              ]}
              value={filters.inquiry_type || ''}
              onChange={(val) => handleFilterChange('inquiry_type', val)}
              placeholder="All"
              searchable={false}
              clearable
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <DataTable
          data={messages}
          columns={columns}
          actions={actions}
          isLoading={isLoading}
          error={error}
          emptyMessage="No contact messages yet"
        />

        {/* Pagination */}
        {meta.last_page > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Showing page {meta.current_page} of {meta.last_page} ({meta.total} total)
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(meta.current_page - 1)}
                disabled={meta.current_page <= 1}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(meta.current_page + 1)}
                disabled={meta.current_page >= meta.last_page}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedMessage && (
        <ContactMessageDetailModal
          message={selectedMessage}
          onClose={() => setSelectedMessage(null)}
          INQUIRY_LABELS={INQUIRY_LABELS}
          updateMessage={updateMessage}
          replyMessage={replyMessage}
          isReplying={isReplying}
          toast={toast}
        />
      )}
    </div>
  );
};

export default AdminContactMessages;
